import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PaymentStep, PaymentData, PaymentMethod, PaymentRequest } from '../types/payment.types';
import PaymentMethodSelector from './PaymentMethodSelector';
import PaymentForm from './PaymentForm';
import OrderSummary from './OrderSummary';
import { processPayment } from '../services/paymentService';
import logo from '../assets/logo.jpeg';

const steps: PaymentStep[] = [
  { id: 1, title: 'Détails de la transaction', completed: false, active: true },
  { id: 2, title: 'Méthode de paiement', completed: false, active: false },
  { id: 3, title: 'Détails du paiement', completed: false, active: false },
  { id: 4, title: 'Confirmation du paiement', completed: false, active: false },
];

// Mock payment data - in a real app, this would come from props or context
const mockPaymentData: PaymentData = {
  amount: 150.00,
  currency: 'USD',
  description: 'Premium Subscription',
  merchantId: 'MASA_MERCHANT_001',
  orderId: `ORDER_${Date.now()}`,
  customerEmail: 'customer@example.com',
  customerPhone: '+1234567890',
  returnUrl: '/success',
  cancelUrl: '/failure',
};

const PaymentGateway: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [paymentData] = useState<PaymentData>(mockPaymentData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    setError(null);
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    setError(null);
  };

  const handleMethodSelect = (method: PaymentMethod) => {
    setSelectedMethod(method);
    handleNext();
  };

  const handlePaymentSubmit = async (paymentDetails: any) => {
    if (!selectedMethod) return;

    setLoading(true);
    setError(null);

    try {
      const request: PaymentRequest = {
        paymentData,
        paymentMethod: selectedMethod,
        paymentDetails,
      };

      const response = await processPayment(request);
      
      if (response.success) {
        handleNext();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Payment processing failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <OrderSummary paymentData={paymentData} onNext={handleNext} />;
      case 1:
        return <PaymentMethodSelector onSelect={handleMethodSelect} />;
      case 2:
        return (
          <PaymentForm
            paymentMethod={selectedMethod!}
            onSubmit={handlePaymentSubmit}
            onBack={handleBack}
            loading={loading}
          />
        );
      case 3:
        return (
          <Box textAlign="center" py={4}>
            <Typography variant="h5" color="primary" gutterBottom>
              Payment Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your payment has been processed successfully.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 3 }}
              onClick={() => window.location.href = paymentData.returnUrl}
            >
              Continue
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom textAlign="center">
          <img src={logo} alt="Masa Payment Gateway" style={{ width: '200px', height: '200px' }} />
        </Typography>

        <Stepper activeStep={currentStep} sx={{ mb: 4 }} color="primary">
          {steps.map((step) => (
            <Step key={step.id}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" mb={3}>
            <CircularProgress />
          </Box>
        )}

        <Box sx={{ minHeight: 400 }}>
          {renderStepContent()}
        </Box>

        {currentStep > 0 && currentStep < 3 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={handleBack} disabled={loading} color="primary">
              Retour
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Step {currentStep + 1} of {steps.length}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentGateway; 